import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthProvider";
import { toast } from "@/hooks/use-toast";
import StatusBadge from "./StatusBadge";
import { Mail, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";

export interface ContactRow {
  id: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  topic: string;
  message: string;
  language: string;
  user_agent: string | null;
  page_url: string | null;
  created_at: string;
  status: string;
  internal_notes: string | null;
  handled_at: string | null;
  handled_by: string | null;
}

interface Props {
  contact: ContactRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (updated: ContactRow) => void;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const ContactDetailDrawer = ({ contact, open, onOpenChange, onUpdated }: Props) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const saveTimer = useRef<number | null>(null);
  const initial = useRef<string>("");

  useEffect(() => {
    setNotes(contact?.internal_notes ?? "");
    initial.current = contact?.internal_notes ?? "";
  }, [contact?.id]);

  // Debounced auto-save notes
  useEffect(() => {
    if (!contact) return;
    if (notes === initial.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      setSavingNotes(true);
      const { data, error } = await supabase
        .from("contact_submissions")
        .update({ internal_notes: notes })
        .eq("id", contact.id)
        .select()
        .single();
      setSavingNotes(false);
      if (error) {
        toast({ title: "Could not save notes", description: error.message, variant: "destructive" });
        return;
      }
      initial.current = notes;
      onUpdated(data as ContactRow);
    }, 700);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [notes, contact?.id]);

  if (!contact) return null;

  const isHandled = contact.status === "handled";

  const toggleStatus = async () => {
    setTogglingStatus(true);
    const next = isHandled
      ? { status: "new", handled_at: null, handled_by: null }
      : { status: "handled", handled_at: new Date().toISOString(), handled_by: user?.id ?? null };
    const { data, error } = await supabase
      .from("contact_submissions")
      .update(next)
      .eq("id", contact.id)
      .select()
      .single();
    setTogglingStatus(false);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    onUpdated(data as ContactRow);
    toast({ title: isHandled ? "Marked as new" : "Marked as handled" });
  };

  const mailto = `mailto:${contact.email}?subject=${encodeURIComponent(
    `Re: ${contact.topic}`
  )}&body=${encodeURIComponent(`Hi ${contact.name},\n\n`)}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <StatusBadge
              label={isHandled ? "Handled" : "New"}
              tone={isHandled ? "success" : "info"}
            />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">
              {contact.language}
            </span>
          </div>
          <SheetTitle className="text-2xl">{contact.topic}</SheetTitle>
          <SheetDescription>{fmtDate(contact.created_at)}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <section className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Name</div>
              <div className="font-medium">{contact.name}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Email</div>
              <a href={`mailto:${contact.email}`} className="font-medium text-primary hover:underline break-all">
                {contact.email}
              </a>
            </div>
            {contact.company && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Company</div>
                <div className="font-medium">{contact.company}</div>
              </div>
            )}
            {contact.phone && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Phone</div>
                <a href={`tel:${contact.phone}`} className="font-medium text-primary hover:underline">
                  {contact.phone}
                </a>
              </div>
            )}
          </section>

          <section>
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Message</div>
            <div className="rounded-md border border-border bg-muted/30 p-4 text-sm whitespace-pre-wrap">
              {contact.message}
            </div>
          </section>

          <section className="flex flex-wrap gap-2">
            <Button asChild>
              <a href={mailto}>
                <Mail className="w-4 h-4 mr-2" /> Reply via email
              </a>
            </Button>
            <Button variant={isHandled ? "outline" : "default"} onClick={toggleStatus} disabled={togglingStatus}>
              {togglingStatus ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : isHandled ? (
                <RotateCcw className="w-4 h-4 mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              {isHandled ? "Reopen" : "Mark as handled"}
            </Button>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="notes" className="text-xs uppercase tracking-wider text-muted-foreground">
                Internal notes
              </Label>
              <span className="text-xs text-muted-foreground">
                {savingNotes ? "Saving…" : notes === initial.current ? "Saved" : "Unsaved"}
              </span>
            </div>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note for your team…"
              className="min-h-[120px]"
            />
          </section>

          <section className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border">
            {contact.page_url && (
              <div>
                <span className="uppercase tracking-wider">Submitted from:</span>{" "}
                <span className="break-all">{contact.page_url}</span>
              </div>
            )}
            {contact.user_agent && (
              <div className="break-all">
                <span className="uppercase tracking-wider">User agent:</span> {contact.user_agent}
              </div>
            )}
            {contact.handled_at && (
              <div>
                <span className="uppercase tracking-wider">Handled at:</span>{" "}
                {fmtDate(contact.handled_at)}
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ContactDetailDrawer;
